<script setup>
// core
import {useTemplateRef} from "vue";
import {useI18n} from "vue-i18n";
import {useInfiniteScroll} from "@vueuse/core";
import useScrollFade from "@/components/Telephony/api/useScrollFade";

// components
import CallNumberView from "./CallNumberView.vue";
import ConnectedNumbersStatus from "./ConnectedNumbersStatus.vue";
import TariffTime from "./TariffTime.vue";
import BaseCopyButton from "@/components/ui/BaseCopyButton.vue";
import BaseFadeScroll from "@/components/ui/BaseFadeScroll.vue";

// composables
import useExtensionsLimit from "./api/useExtensionsLimit";

const {getExtensionsInfo, isUnlimited} = useExtensionsLimit()

const props = defineProps({
    data: {
        type: Array,
        required: true,
        default: () => []
    },
    // a page is on its way: the loader row sits at the foot
    loading: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(["loadMore", "openSettingsDialog", "openSelectSipDialog"]);

const {t: $t} = useI18n()
const scrollComponent = useTemplateRef('scrollComponent')
const {isBottom} = useScrollFade(scrollComponent, () => props.data?.length)

// the same paging as ConnectedNumbersTable
useInfiniteScroll(
    scrollComponent,
    () => {
        emit("loadMore");
    },
    {
        distance: 50,
        interval: 300,
    }
);

// number, status, tariff, api key, sip trunks, settings; paid until moves under the tariff
const COLUMNS = [0, 3, 1, 4, 5, 6]

const onSettingsClick = (item) => {
    emit('openSettingsDialog', item)
}

const onSelectSipClick = (item) => {
    emit('openSelectSipDialog', item)
}
</script>

<template>
    <div class="connected-numbers-n">
        <div class="connected-numbers-n__head">
            <span v-for="index in COLUMNS" :key="index" class="headed_text">
                {{ $t(`telephony.connected_numbers.columns.${index}`) }}
            </span>
        </div>

        <ul class="connected-numbers-n__list" ref="scrollComponent">
            <li v-for="(item, i) in props.data" :key="i" class="connected-numbers-n__row">
                <CallNumberView class="connected-numbers-n__number" :number="item.number"/>

                <ConnectedNumbersStatus class="connected-numbers-n__status" :status="item.status"/>

                <span class="tariff">
                    <span class="tariff__name">{{ item.tariff ?? '-' }}</span>
                    <span class="tariff__time"><TariffTime :item="item"/></span>
                    <!-- !TODO: paid_until is not in the API response yet -->
                    <span class="tariff__time">
                        {{ $t('telephony.connected_numbers.paid_until_short', {date: item.paid_until ?? '-'}) }}
                    </span>
                </span>

                <span class="connected-numbers-n__key">
                    <BaseFadeScroll class="connected-numbers-n__key-value" :text="item.api_key"/>
                    <BaseCopyButton
                        :value="item.api_key"
                        :title="$t('telephony.connected_numbers.copy_api_key')"
                        toast="telephony.sip.copied"
                    />
                </span>

                <span class="extensions">
                    <span class="extensions__count">{{ getExtensionsInfo(item).count }}<template v-if="getExtensionsInfo(item).limit !== null">&nbsp;/&nbsp;<BIconInfinity
                            v-if="isUnlimited(item)"
                            class="extensions__infinity"
                        /><template v-else>{{ getExtensionsInfo(item).limit }}</template></template>
                    </span>
                    <button type="button" class="inside_block" @click="onSelectSipClick(item)">
                        <BIconHeadset class="icon"/>
                    </button>
                </span>

                <button type="button" class="inside_block" @click="onSettingsClick(item)">
                    <BIconGear class="icon"/>
                </button>
            </li>

            <li class="connected-numbers-n__info">
                <slot name="information_column"/>
            </li>
            <li class="scroll-fade" :class="{ 'is-bottom': isBottom || loading }" aria-hidden="true"></li>
        </ul>
    </div>
</template>

<style scoped lang="scss">
@use "@/styles/scroll-fade.scss";
// the desktop table with seven columns folded into six, for 900–1199px
.connected-numbers-n {
    // the column minimums add up to ~655px; 760px is left at a 900px window
    --columns: minmax(150px, 1.3fr) minmax(120px, 1fr) minmax(80px, 0.8fr) minmax(110px, 1fr) minmax(95px, 0.8fr) minmax(100px, 0.6fr);

    &__head,
    &__row {
        display: grid;
        grid-template-columns: var(--columns);
        gap: 10px;
        align-items: center;
        padding: 0 16px;
        text-align: center;
    }

    // thead of table.scss
    &__head {
        margin: 5px 0 2px;
        padding-top: 20px;
        padding-bottom: 13px;
        border-radius: 11px;
        background-color: var(--block-color);
    }

    &__list {
        max-height: 293px;
        overflow-y: auto;
        margin: 5px 0;
        padding: 0;
        list-style: none;
    }

    &__row {
        padding-top: 7px;
        padding-bottom: 7px;
        border-bottom: 1px solid var(--block-color);
        border-radius: 10px;
        transition: background-color 0.3s ease;

        > * {
            justify-self: center;
        }

        &:hover {
            background-color: #f4f3fc85;
        }
    }

    // .number .item of ConnectedNumbersTable: a fixed box centred in the cell,
    // so the numbers of all rows start on one line. Nested to outweigh the
    // centring in CallNumberView's own scoped styles
    &__row &__number {
        width: 150px;
        justify-content: flex-start;
        gap: 10px;
    }

    // a td of the table: the button fills the cell up to its 198px cap
    &__status {
        justify-self: stretch;
    }

    &__key {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        min-width: 0;
        max-width: 100%;
    }

    // BaseFadeScroll fades the key only when it is cut off
    &__key-value {
        flex: 0 1 auto;
        min-width: 0;

        font-family: 'Gilroy', sans-serif;
        font-size: 14px;
        font-weight: 500;
        letter-spacing: 0.05rem;
        color: var(--text-third-color);
    }

    &__info {
        display: block;
    }
}

.scroll-fade {
    @include scroll-fade.scroll-fade;
}

.headed_text {
    font-family: Gilroy;
    font-weight: 500;
    text-transform: uppercase;
    color: var(--text-light-color);
}

.tariff {
    display: flex;
    flex-direction: column;
    gap: 2px;

    &__name {
        font-weight: 500;
        color: var(--text-second-color);

        &::first-letter {
            text-transform: uppercase;
        }
    }

    &__time {
        font-size: 0.85rem;
        white-space: nowrap;
        color: var(--text-light-color);
    }
}

.extensions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;

    &__count {
        white-space: nowrap;
        min-width: 44px;
        text-align: center;
    }

    &__infinity {
        display: inline-block;
        font-size: 1.1em;
        vertical-align: -0.15em;
    }
}

// .settings .inside_block of table.scss
.inside_block {
    min-width: 35px;
    width: 35px;
    height: 35px;
    padding: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    border: 1px solid var(--general-color);
    border-radius: 9px;
    background-color: transparent;
    cursor: pointer;
    transition: 0.25s;

    .icon {
        font-size: 22px;
        color: var(--general-color);
        transition: 0.25s;
    }

    &:hover {
        background-color: var(--general-color);

        .icon {
            fill: white;
        }
    }
}
</style>
